import { yupResolver } from '@hookform/resolvers/yup';
import SearchIcon from '@mui/icons-material/Search';
import { CardHeader, Grid, IconButton } from '@mui/material';
import moment from 'moment';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { PeriodFilterValuesEnum } from 'utils/periodEnum';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { clearFilters, setFilters, setOnlineContractingList } from 'v3/data/store/onlineContracting/actions';
import { CreditSimulationOriginEnum, OnlineContractingStateEnum } from 'v3/data/store/onlineContracting/enums';
import { CHANNEL_LABEL_MAP } from 'v3/data/store/onlineContracting/mapping';
import { filtersInitialState } from 'v3/data/store/onlineContracting/state';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/online/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { MiniButton } from 'v3/shared/components/Button/MiniButton';
import { OnlineContractingStatusMap } from 'v3/shared/components/Filters/OnlineFilter/enum';
import { filterRequestFactory, getDateRange } from 'v3/shared/components/Filters/OnlineFilter/factory';
import { CustomFilterType } from 'v3/shared/components/Filters/OnlineFilter/types';
import { DatePickerFormController } from 'v3/shared/components/Inputs/DatePickerFormController';
import { DropdownSelectFormController } from 'v3/shared/components/Inputs/DropdownSelectFormController';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { onlineContractingFilterSchema } from 'v3/shared/schemas/onlineContractingFilter.schema';
import { BodyLarge } from 'v3/shared/typography/BodyLarge';
import { AlignButtons, CardContentMain, CardContentStyled, CardStyled, DownIcon, GridStyled, UpIcon } from './styles';

const NON_FILTERABLE_STATUSES = [
  OnlineContractingStateEnum.ProcessingCancellation,
  OnlineContractingStateEnum.ContractingCanceled,
  OnlineContractingStateEnum.ExpiredKey,
];

const statusOptions = Object.values(OnlineContractingStateEnum)
  .filter((status) => !NON_FILTERABLE_STATUSES.includes(status))
  .map((status) => ({
    value: status,
    label: OnlineContractingStatusMap[status],
  }));

const channelOptions = [
  { value: CreditSimulationOriginEnum.InApp, label: CHANNEL_LABEL_MAP[CreditSimulationOriginEnum.InApp] },
  { value: CreditSimulationOriginEnum.LOM, label: CHANNEL_LABEL_MAP[CreditSimulationOriginEnum.LOM] },
];

export const OnlineFilters = () => {
  const [expanded, setExpanded] = useState(false);
  const { isTablet } = useBreakpoints();
  const { filter } = useStoreSelector((store) => store.onlineContracting);
  const { rowsPerPage, page, ...customFilters } = filter;
  const [datesDisabled, setDatesDisabled] = useState(false);

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
  } = useForm<CustomFilterType>({
    mode: 'onChange',
    values: customFilters,
    reValidateMode: 'onChange',
    resolver: yupResolver(onlineContractingFilterSchema) as Resolver<CustomFilterType>,
  });

  const getContractingList = useCallback(
    async (isResetFilter?: boolean) => {
      const filterValues = getValues();
      const filtersFormated = isResetFilter ? {} : filterRequestFactory(filterValues, filter);

      try {
        const response = await api.getContractingListInApp({
          ...filtersFormated,
          rowsPerPage,
          page,
        });
        setOnlineContractingList(response.data);
      } catch (error) {
        showErrorDialog('Erro ao obter lista de contratações', error);
      }
    },
    [page, rowsPerPage],
  );

  useEffect(() => {
    getContractingList();
  }, [getContractingList]);

  const handlePeriodChange = (value: keyof typeof PeriodFilterValuesEnum) => {
    if (!value?.length) {
      setDatesDisabled(false);
      setFilters({ ...filter, startDate: '', finalDate: '', period: value });
    } else {
      setDatesDisabled(true);
      const { startDate, finalDate } = getDateRange(value);
      setFilters({ ...filter, startDate, finalDate, period: value });
    }
  };

  const handleClear = async () => {
    clearFilters();
    reset(filtersInitialState);
    setExpanded(false);
    setDatesDisabled(false);
    getContractingList(true);
  };

  const startDate = useWatch({
    control,
    name: 'startDate',
  });

  const finalDate = useWatch({
    control,
    name: 'finalDate',
  });

  return (
    <CardStyled>
      <CardContentStyled>
        <BodyLarge>Filtros</BodyLarge>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }} component="div">
            <InputFormController
              data-testid="input-search"
              formControl={control}
              formControlName="search"
              label="CPF ou OrderID"
              placeholder="Informe um CPF ou OrderID"
              fullWidth
              startAdornmentIcon={SearchIcon}
              onChangeInput={(search) => setFilters({ ...filter, search })}
            />
          </Grid>

          <GridStyled size={{ xs: 12, sm: 6, md: 3, lg: 2, xl: 3 }}>
            <DropdownSelectFormController
              data-testid="select-form-controller-channel"
              formControl={control}
              fullWidth
              formControlName="origin"
              label="Canal"
              selectValues={channelOptions}
              onChangeSelect={(origin) => setFilters({ ...filter, origin })}
            />
          </GridStyled>

          <GridStyled size={{ xs: 12, sm: 6, md: 3, lg: 2, xl: 3 }}>
            <DropdownSelectFormController
              data-testid="select-form-controller-status"
              formControl={control}
              fullWidth
              formControlName="status"
              label="Status do Financiamento"
              selectValues={statusOptions}
              onChangeSelect={(status) => setFilters({ ...filter, status })}
            />
          </GridStyled>

          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }} order={{ xs: 99, sm: 'unset' }} component="div">
            <AlignButtons>
              <IconButton onClick={() => setExpanded(!expanded)}>{expanded ? <UpIcon /> : <DownIcon />}</IconButton>
              {isTablet ? (
                <MiniButton icon={SearchIcon} handleClick={getContractingList} disabled={!isValid} />
              ) : (
                <FlexButton text="Pesquisar" size="small" handleClick={getContractingList} disabled={!isValid} />
              )}

              <MiniButton text="Limpar" $backgroundColor="transparent" handleClick={handleClear} />
            </AlignButtons>
          </Grid>

          <Grid size={{ xs: 12 }} component="div">
            {expanded ? (
              <CardContentMain>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }} component="div">
                    <DropdownSelectFormController
                      data-testid="select-form-controller-period"
                      formControl={control}
                      formControlName="period"
                      label="Período"
                      selectValues={PeriodFilterValuesEnum}
                      enumType
                      onChangeSelect={handlePeriodChange}
                    />
                  </Grid>

                  {!datesDisabled ? (
                    <Fragment>
                      <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }} component="div">
                        <DatePickerFormController
                          formControl={control}
                          formControlName="startDate"
                          label="Início"
                          showIcon
                          defaultValue={filter.startDate ? moment(filter.startDate) : null}
                          onChangeDatePicker={(newStartDate) => setFilters({ ...filter, startDate: newStartDate })}
                          disableFuture
                          maxDate={finalDate ? moment(finalDate) : undefined}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }} component="div">
                        <DatePickerFormController
                          formControl={control}
                          formControlName="finalDate"
                          label="Fim"
                          showIcon
                          defaultValue={filter.finalDate ? moment(filter.finalDate) : null}
                          onChangeDatePicker={(newFinalDate) => setFilters({ ...filter, finalDate: newFinalDate })}
                          disableFuture
                          minDate={startDate ? moment(startDate) : undefined}
                        />
                      </Grid>
                    </Fragment>
                  ) : null}
                </Grid>
              </CardContentMain>
            ) : null}
          </Grid>
        </Grid>
      </CardContentStyled>
    </CardStyled>
  );
};
